import type { UploadJob } from "../../contexts/uploadQueue.types";
import type { AdvertisingVideoFields } from "../../utils/translationAdapters/advertising";
import type { VideoUploadZoneState } from "../form/VideoUploadZone";

/**
 * The two sources the zone reads: the videos come from the server, while
 * `isDefaultLanguage` comes from the form, where "set as default language" can
 * change it before any save.
 */
export function deriveVideoUploadZoneState({
  video,
  defaultLanguageVideo,
  isDefaultLanguage,
  job,
  defaultLanguageName,
}: {
  video: AdvertisingVideoFields | undefined;
  defaultLanguageVideo: AdvertisingVideoFields | undefined;
  isDefaultLanguage: boolean;
  job: UploadJob | undefined;
  defaultLanguageName: string;
}): VideoUploadZoneState {
  if (job?.status === "pending") return { status: "queued" };

  if (job?.status === "uploading") {
    return { status: "uploading", progress: job.progress };
  }

  if (job?.status === "error") return { status: "error" };

  if (video?.video_url) {
    return {
      status: "ready",
      filename: video.filename,
      videoUrl: video.video_url,
      isDeletable: !isDefaultLanguage,
    };
  }

  if (isDefaultLanguage || !defaultLanguageVideo?.video_url) {
    return { status: "empty", isDefaultLanguage };
  }

  return {
    status: "inherited",
    defaultLanguageName,
    filename: defaultLanguageVideo.filename,
    videoUrl: defaultLanguageVideo.video_url,
  };
}

/**
 * True while an upload of this campaign has not reached the cache yet. A
 * `completed` advertising job is announced only once the campaign query has
 * refetched, so by then it is already in sync and deliberately not matched.
 */
export function hasUnsyncedAdvertisingUpload(
  jobs: UploadJob[],
  campaignUuid: string,
): boolean {
  return jobs.some(
    (job) =>
      job.kind === "advertisingVideo" &&
      job.campaignUuid === campaignUuid &&
      (job.status === "pending" || job.status === "uploading"),
  );
}
